import { useCallback, useSyncExternalStore } from 'react';

import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router';

import CookieBannerFull from '@/app/components/CookieBanner/CookieBannerFull/index';
import CookieBannerMobile from '@/app/components/CookieBanner/CookieBannerMobile/index';
import { clearSelection } from '@/features/symbol-selector';
import { clock } from '@/services/clock';
import { logout } from '@/store/authActions';
import { useAppDispatch } from '@/store/hooks';

const MOBILE_COOKIE_BANNER_QUERY = '(max-width: 899px)';

const subscribeToCookieBannerBreakpoint = (onStoreChange: () => void) => {
  if (typeof window.matchMedia !== 'function') return () => undefined;

  const mediaQuery = window.matchMedia(MOBILE_COOKIE_BANNER_QUERY);
  mediaQuery.addEventListener('change', onStoreChange);
  return () => mediaQuery.removeEventListener('change', onStoreChange);
};

const getCookieBannerBreakpointSnapshot = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_COOKIE_BANNER_QUERY).matches;

const useMobileCookieBanner = () =>
  useSyncExternalStore(
    subscribeToCookieBannerBreakpoint,
    getCookieBannerBreakpointSnapshot,
    () => false
  );

const CookieBanner = () => {
  const [cookies, setCookie] = useCookies(['cookies-accepted']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const showMobileBanner = useMobileCookieBanner();

  const handleDecline = useCallback(() => {
    dispatch(clearSelection());
    dispatch(logout());
    navigate('/login');
  }, [dispatch, navigate]);

  const handleAccept = useCallback(() => {
    const expires = clock.date();
    expires.setFullYear(expires.getFullYear() + 2);

    setCookie('cookies-accepted', true, {
      expires,
      path: '/',
      sameSite: 'lax',
      secure: window.location.protocol === 'https:'
    });
  }, [setCookie]);

  if (cookies['cookies-accepted']) return null;

  return showMobileBanner ? (
    <CookieBannerMobile
      handleAccept={handleAccept}
      handleDecline={handleDecline}
    />
  ) : (
    <CookieBannerFull
      handleAccept={handleAccept}
      handleDecline={handleDecline}
    />
  );
};

export default CookieBanner;
